/**
 * Small form controls from the DESIGN.md §5 inventory: SegmentedControl (radio group as
 * buttons), ColorPicker (brand setting) and FileUpload (button + hidden file input).
 */
import type { LucideIcon } from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/utils';
import { Button, type ButtonProps } from './button';
import { Input } from './input';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

export interface SegmentedControlProps<T extends string> {
  label: string;
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Small buttons and no visible label (the label stays the accessible name), e.g. a mode switch above an editor. */
  compact?: boolean;
}

/** A labelled radio group rendered as buttons (theme, density, language, …). Arrow keys move the choice. */
export function SegmentedControl<T extends string>({ label, value, options, onChange, disabled, compact = false }: SegmentedControlProps<T>) {
  const move = (event: KeyboardEvent<HTMLElement>, index: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = options[(index + step + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    (event.currentTarget.parentElement?.children[options.indexOf(next)] as HTMLElement | undefined)?.focus();
  };
  return (
    <div className="flex flex-col gap-2">
      {!compact && <span className="text-sm font-medium">{label}</span>}
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map(({ value: option, label: optionLabel, icon: Icon }, index) => (
          <Button
            key={option}
            role="radio"
            aria-checked={value === option}
            variant="outline"
            size={compact ? 'sm' : 'md'}
            disabled={disabled}
            onClick={() => onChange(option)}
            onKeyDown={(event) => move(event, index)}
            className={cn(value === option && 'border-primary bg-primary/10 text-primary hover:bg-primary/15')}
          >
            {Icon && <Icon />}
            {optionLabel}
          </Button>
        ))}
      </div>
    </div>
  );
}

const HEX = /^#[0-9a-fA-F]{6}$/;

export interface ColorPickerProps {
  id?: string;
  /** `#rrggbb`. */
  value: string;
  onChange: (value: string) => void;
  /** Accessible name of the swatch (the text field uses the form label via `id`). */
  swatchLabel: string;
  disabled?: boolean;
  invalid?: boolean;
}

/** Native colour swatch + hex field. `onChange` only fires with complete `#rrggbb` values. */
export function ColorPicker({ id, value, onChange, swatchLabel, disabled, invalid }: ColorPickerProps) {
  const [text, setText] = useState(value);
  // Follow outside changes (swatch, reset) without an effect: adjust state while rendering.
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setText(value);
  }
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label={swatchLabel}
        value={HEX.test(value) ? value.toLowerCase() : '#000000'}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="h-control w-12 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <Input
        id={id}
        value={text}
        disabled={disabled}
        aria-invalid={invalid || !HEX.test(text) || undefined}
        spellCheck={false}
        autoComplete="off"
        maxLength={7}
        className="w-32 font-mono"
        onChange={(event) => {
          setText(event.target.value);
          if (HEX.test(event.target.value)) onChange(event.target.value.toLowerCase());
        }}
      />
    </div>
  );
}

export interface FileUploadProps extends Omit<ButtonProps, 'onSelect' | 'children'> {
  /** e.g. `image/svg+xml,image/png` or `.json,application/json`. */
  accept: string;
  onSelect: (file: File) => void;
  children: ReactNode;
}

/** A button that opens the file chooser; the same file can be chosen again. */
export function FileUpload({ accept, onSelect, children, variant = 'outline', ...props }: FileUploadProps) {
  const input = useRef<HTMLInputElement>(null);
  const inputId = useId();
  return (
    <>
      <input
        ref={input}
        id={inputId}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onSelect(file);
        }}
      />
      <Button variant={variant} {...props} onClick={() => input.current?.click()}>
        {children}
      </Button>
    </>
  );
}
